import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscriber, from, of } from 'rxjs';
import { debounceTime, mergeMap } from 'rxjs/operators';
import { Inspection } from '../../shared/interfaces';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';

@Component({
  selector: 'app-inspection-search',
  templateUrl: './inspection-search.component.html',
  styleUrls: ['./inspection-search.component.scss'],
})
export class InspectionSearchComponent implements OnInit {
  @Input() underwritingSearch: boolean;

  _searchField = '';
  get searchField() {
    return this._searchField;
  }
  set searchField(search: string) {
    this._searchField = search;

    // Update URL search
    this.router.navigate([], { queryParams: { search } });
  }

  // Inspection search typeahead DataSource
  dataSource: Observable<any[]>;

  getStatusAsText(inspection: Inspection) {
    const plus2weeks = new Date();
    plus2weeks.setDate(plus2weeks.getDate() + 14);

    const overdue = inspection.dueDate && new Date(inspection.dueDate) < new Date();

    switch (inspection.status) {
      case InspectionStatus.NO_DATE_ASSIGNED:
        return 'No date assigned';

      case InspectionStatus.ASSIGNMENT_MADE:
      case InspectionStatus.WAITING:
        if (new Date(inspection.dueDate) && new Date(inspection.dueDate) < plus2weeks) {
          if (!inspection.vendor) {
            return 'Ready to be assigned';
          }

          if (overdue && inspection.inspector) {
            return 'Due completion';
          } else {
            return 'Assignment made';
          }
        } else {
          return 'Waiting';
        }

      case InspectionStatus.NEEDS_REVIEW:
      case InspectionStatus.INVALID_SYNC:
        return 'Completed and needs review';

      case InspectionStatus.REJECTED:
        return 'Rejected';
      case InspectionStatus.ACCEPTED:
      case InspectionStatus.DEPRECATED_COMPLETED:
        return 'Accepted';
    }
  }

  constructor(
    public inspectionService: InspectionService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    // Inspection search logic
    this.dataSource = new Observable((observer: Subscriber<string>) => {
      observer.next(this.searchField);
    }).pipe(
      debounceTime(200),
      mergeMap((search: string) => {
        if (search) {
          // Regular expression for matching
          const regex = new RegExp(search, 'i');
          if (this.underwritingSearch) {
            const underwritingClosed = this.inspectionService
              .underwritingClosed({ search, pageSize: 10 })
              .then(results => {
                return this.transformResults(results[0], regex);
              });
            return from(underwritingClosed);
          } else {
            // Fetch results from DB
            const allInspections = this.inspectionService.list({ search, pageSize: 10 }).then(results => {
              return this.transformResults(results[0], regex);
            });
            return from(allInspections);
          }
        } else {
          // Clear results
          return of([]);
        }
      }),
    );

    this.activatedRoute.queryParams.subscribe(params => {
      this.searchField = params['search'];
    });
  }

  transformResults(results: any[], regex: RegExp): any[] {
    return results.map(inspection => ({
      ...inspection,
      matches: {
        customer: inspection.customerName.match(regex),
        vendor: inspection.vendor && inspection.vendor.name.match(regex),
        inspector: inspection.inspector && inspection.inspector.name.match(regex),
        location: inspection.address.match(regex) || inspection.city.match(regex) || inspection.state.match(regex),
      },
    }));
  }

  openInNewWindow(match) {
    // default behavior
    // (click)="typeaheadTemplateMethods.selectMatch(match, $event)"
    window.open('/inspection/vendor/form/' + match.item.id);
  }

  statusColor(status) {
    switch (status) {
      case InspectionStatus.WAITING:
        return '#F5A100';
      case InspectionStatus.REJECTED:
        return '#DC3E3E';
      case InspectionStatus.ACCEPTED:
      case InspectionStatus.DEPRECATED_COMPLETED:
        return '#17A6DB';
    }
    return '#5c5c5c';
  }
}
